import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CloudDownload as DownloadIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  downloadOrderTechPack,
  getManufacturingQueue,
  getOrderStatus,
  updateOrderProductionStatus,
} from '../../api/partnerPortal';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'queued', label: 'Queued' },
  { key: 'in_production', label: 'In Production' },
  { key: 'quality_check', label: 'Quality Check' },
  { key: 'completed', label: 'Completed' },
  { key: 'shipped', label: 'Shipped' },
];

const STATUS_OPTIONS = [
  { value: 'queued', label: 'Queued' },
  { value: 'in_production', label: 'In Production' },
  { value: 'quality_check', label: 'Quality Check' },
  { value: 'completed', label: 'Completed' },
  { value: 'shipped', label: 'Shipped' },
];

const PRIORITY_COLORS = {
  rush: 'error',
  standard: 'default',
};

const formatDate = (value, fallback = '—') => {
  if (!value) return fallback;
  try {
    return format(new Date(value), 'MMM d, yyyy • h:mm a');
  } catch {
    return fallback;
  }
};

const formatRelativeTime = (value, fallback = '—') => {
  if (!value) return fallback;
  try {
    return formatDistanceToNowStrict(new Date(value), { addSuffix: true });
  } catch {
    return fallback;
  }
};

const PartnerOrdersPage = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const highlightId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('highlight');
  }, [location.search]);

  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [productionNotes, setProductionNotes] = useState('');
  const [estimatedCompletion, setEstimatedCompletion] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('queued');
  const [detailError, setDetailError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const {
    data,
    isLoading,
    isFetching,
    refetch: refetchQueue,
    error,
  } = useQuery(['partner-manufacturing-queue'], getManufacturingQueue, {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60,
  });

  const [orderDetail, setOrderDetail] = useState(null);

  const orderDetailQuery = useMutation((orderId) => getOrderStatus(orderId), {
    onSuccess: (response) => {
      setOrderDetail(response);
      setSelectedStatus(response.status);
      setEstimatedCompletion(
        response.manufacturing?.estimated_completion
          ? new Date(response.manufacturing.estimated_completion).toISOString().slice(0, 16)
          : ''
      );
      setProductionNotes(response.manufacturing?.notes ?? '');
    },
    onError: (err) => {
      setDetailError(
        err?.response?.data?.error || err?.message || 'Failed to load order details.'
      );
    },
  });

  const updateStatusMutation = useMutation(
    ({ orderId, payload }) => updateOrderProductionStatus(orderId, payload),
    {
      onSuccess: async () => {
        await refetchQueue();
        queryClient.invalidateQueries(['partner-dashboard']);
      },
      onError: (err) => {
        setDetailError(
          err?.response?.data?.error || err?.message || 'Unable to update production status.'
        );
      },
    }
  );

  const queue = data?.queue ?? [];
  const summary = data?.queue_summary;
  const totalOrders = data?.total_orders ?? queue.length;

  const filteredQueue = useMemo(() => {
    if (statusFilter === 'all') return queue;
    return queue.filter((item) => (item.status || 'queued') === statusFilter);
  }, [queue, statusFilter]);

  const handleRefresh = async () => {
    await refetchQueue();
  };

  const handleSelectOrder = async (orderId) => {
    setDetailError(null);
    setSelectedOrderId(orderId);
    setOrderDetail(null);
    setProductionNotes('');
    setEstimatedCompletion('');
    await orderDetailQuery.mutateAsync(orderId);
  };

  const handleCloseDialog = () => {
    setSelectedOrderId(null);
    setOrderDetail(null);
    setDetailError(null);
    setProductionNotes('');
    setEstimatedCompletion('');
  };

  const handleDownloadTechPack = async () => {
    if (!selectedOrderId) return;
    setDownloading(true);
    try {
      const response = await downloadOrderTechPack(selectedOrderId);
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/x-yaml',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const disposition = response.headers['content-disposition'];
      let filename = `${selectedOrderId}_techpack.yaml`;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) {
          filename = match[1];
        }
      }
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setDetailError(
        err?.response?.data?.error || err?.message || 'Unable to download tech pack.'
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrderId) return;
    setDetailError(null);
    try {
      let isoCompletion;
      if (estimatedCompletion) {
        const parsed = new Date(estimatedCompletion);
        if (!Number.isNaN(parsed.getTime())) {
          isoCompletion = parsed.toISOString();
        }
      }

      await updateStatusMutation.mutateAsync({
        orderId: selectedOrderId,
        payload: {
          status: selectedStatus,
          production_notes: productionNotes || undefined,
          estimated_completion: isoCompletion,
        },
      });
      await orderDetailQuery.mutateAsync(selectedOrderId);
    } catch (err) {
      // error state handled in mutation onError
    }
  };

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Orders Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track production progress, update statuses, and download tech packs for orders assigned
            to your team.
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" gap={1}>
          {isFetching && <CircularProgress size={20} />}
          <Tooltip title="Refresh queue">
            <span>
              <IconButton onClick={handleRefresh} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error">
          {error?.response?.data?.error ||
            error?.message ||
            'Failed to load manufacturing queue data.'}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4} lg={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
            }}
          >
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" gap={1}>
                <AssignmentIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Queue Summary
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Active orders</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {totalOrders}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">High priority</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {summary?.high_priority ?? 0}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Standard</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {summary?.standard_priority ?? 0}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Avg. production time</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {summary?.average_production_time ?? '—'}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8} lg={9}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Tabs
              value={statusFilter}
              onChange={(_, value) => setStatusFilter(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              {STATUS_TABS.map((tab) => (
                <Tab key={tab.key} label={tab.label} value={tab.key} />
              ))}
            </Tabs>

            {isLoading ? (
              <Box
                sx={{
                  minHeight: 220,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress />
              </Box>
            ) : filteredQueue.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">
                  No orders match the selected filter. Studio Sivad will assign new tech packs as
                  campaigns progress.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                    '& th, & td': {
                      textAlign: 'left',
                      py: 1.5,
                      px: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                    '& tbody tr:hover': {
                      backgroundColor: 'action.hover',
                      cursor: 'pointer',
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Queue Position</th>
                      <th>Estimated Production Time</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQueue.map((item) => {
                      const isHighlighted = highlightId && highlightId === item.orderId;
                      return (
                        <tr
                          key={item.orderId}
                          onClick={() => handleSelectOrder(item.orderId)}
                          style={{
                            backgroundColor: isHighlighted ? 'rgba(29, 78, 216, 0.08)' : undefined,
                          }}
                        >
                          <td>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {item.orderId}
                              </Typography>
                            </Stack>
                          </td>
                          <td>
                            <Chip
                              label={item.status || 'queued'}
                              size="small"
                              color={
                                item.status === 'in_production'
                                  ? 'success'
                                  : item.status === 'quality_check'
                                    ? 'secondary'
                                    : item.status === 'completed'
                                      ? 'primary'
                                      : 'default'
                              }
                            />
                          </td>
                          <td>
                            <Chip
                              label={item.priority || 'standard'}
                              size="small"
                              color={PRIORITY_COLORS[item.priority] || 'default'}
                              variant="outlined"
                            />
                          </td>
                          <td>{item.queue_position ?? '—'}</td>
                          <td>{item.estimated_production_time || '—'}</td>
                          <td>
                            <Typography variant="caption" color="text.secondary">
                              {formatRelativeTime(item.created_at)}
                            </Typography>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={Boolean(selectedOrderId)}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon fontSize="small" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Order {selectedOrderId}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
          {orderDetailQuery.isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {detailError && <Alert severity="error">{detailError}</Alert>}

          {orderDetail && (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: 'grid',
                  gap: 1,
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Overview
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {orderDetail.status}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Queue Position
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {orderDetail.manufacturing?.queue_position ?? '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Estimated Completion
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatDate(orderDetail.manufacturing?.estimated_completion)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: 'grid',
                  gap: 2,
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Update Production Status
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Status"
                      fullWidth
                      value={selectedStatus}
                      onChange={(event) => setSelectedStatus(event.target.value)}
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      label="Estimated completion"
                      type="datetime-local"
                      fullWidth
                      value={estimatedCompletion}
                      onChange={(event) => setEstimatedCompletion(event.target.value)}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant="caption" color="text.secondary">
                              Local time
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Production notes"
                      multiline
                      minRows={3}
                      fullWidth
                      value={productionNotes}
                      onChange={(event) => setProductionNotes(event.target.value)}
                      placeholder="Share progress updates, blockers, or QA notes"
                    />
                  </Grid>
                </Grid>
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleUpdateStatus}
                    disabled={updateStatusMutation.isLoading}
                  >
                    {updateStatusMutation.isLoading ? 'Updating...' : 'Save status'}
                  </Button>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Tech Pack</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Download the latest Studio Sivad tech pack for this order. Only available when
                      the file has been generated.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadTechPack}
                    disabled={downloading}
                  >
                    {downloading ? 'Preparing...' : 'Download'}
                  </Button>
                </Stack>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerOrdersPage;
